import { ColumnType, TableData, SpecialAttributeItem } from './types';

const TABLE_PRESET_VALUE_MARKER = '表格预设值';

/**
 * 解析表格详情数据
 */
export function parseTableDetail(item: any): TableData {
  // 解析检测数据格式描述
  let dataFormatDesc: any = {};
  try {
    dataFormatDesc = item.检测数据格式描述 || (item.检测数据格式描述JSON ? JSON.parse(item.检测数据格式描述JSON) : {});
  } catch (e) {
    console.error('解析检测数据格式描述失败:', e);
  }
  
  // 解析横向标题
  let horizontalHeaders: string[] = [];
  try {
    horizontalHeaders = dataFormatDesc.横向标题 || 
                     (dataFormatDesc.横向标题JSON ? JSON.parse(dataFormatDesc.横向标题JSON) : []) ||
                     (item.横向标题 && Array.isArray(item.横向标题) ? item.横向标题 : []);
    if (horizontalHeaders.length === 0) {
      horizontalHeaders = ['列1'];
    }
  } catch (e) {
    horizontalHeaders = ['列1'];
  }
  
  // 解析纵向标题
  let verticalHeaders: string[] = [];
  try {
    verticalHeaders = dataFormatDesc.纵向标题 || 
                     (dataFormatDesc.纵向标题JSON ? JSON.parse(dataFormatDesc.纵向标题JSON) : []) ||
                     (item.纵向标题 && Array.isArray(item.纵向标题) ? item.纵向标题 : []);
    if (verticalHeaders.length === 0) {
      verticalHeaders = ['行1'];
    }
  } catch (e) {
    verticalHeaders = ['行1'];
  }
  
  // 解析分区线（columnBorders[i]表示第i列左边有分界线）
  let columnBorders: boolean[] = [];
  try {
    columnBorders = dataFormatDesc.分区线 || 
                   (dataFormatDesc.分区线JSON ? JSON.parse(dataFormatDesc.分区线JSON) : []) ||
                   (item.分区线 && Array.isArray(item.分区线) ? item.分区线 : []);
    // 确保长度匹配横向标题
    while (columnBorders.length < horizontalHeaders.length) {
      columnBorders.push(false);
    }
  } catch (e) {
    columnBorders = new Array(horizontalHeaders.length).fill(false);
  }

  // 解析分区标题
  // 如果数组长度 > 横向标题长度，说明第一个元素是第一分区名称
  // sectionNames[-1] 表示第一分区名称
  // sectionNames[i] 表示第 i 列右边分界线后的分区名称 
  let firstSectionName = '';
  let sectionNamesForColumns: string[] = [];
  try {
    const rawSectionNames =
      dataFormatDesc.分区标题 ||
      (dataFormatDesc.分区标题JSON ? JSON.parse(dataFormatDesc.分区标题JSON) : null) ||
      dataFormatDesc.分区名称 ||
      (dataFormatDesc.分区名称JSON ? JSON.parse(dataFormatDesc.分区名称JSON) : null) ||
      (item.分区标题 && Array.isArray(item.分区标题) ? item.分区标题 : null);

    if (Array.isArray(rawSectionNames)) {
      if (rawSectionNames.length > horizontalHeaders.length) {
        // 第一个元素是第一分区名称
        firstSectionName = rawSectionNames[0] || '';
        sectionNamesForColumns = rawSectionNames.slice(1);
      } else {
        sectionNamesForColumns = rawSectionNames;
      }
    }

    // 确保长度匹配横向标题
    while (sectionNamesForColumns.length < horizontalHeaders.length) {
      sectionNamesForColumns.push('');
    }
  } catch (e) {
    sectionNamesForColumns = new Array(horizontalHeaders.length).fill('');
  }

  // 转换为 Record<number, string> 格式
  const sectionNames: Record<number, string> = {};
  if (firstSectionName) {
    sectionNames[-1] = firstSectionName;
  }
  sectionNamesForColumns.forEach((name, index) => {
    if (name) {
      sectionNames[index] = name;
    }
  });
  
  // 解析横向数字验证（列类型）
  let numberValidation: boolean[] = [];
  try {
    numberValidation = dataFormatDesc.横向数字验证 || 
                      (dataFormatDesc.横向数字验证JSON ? JSON.parse(dataFormatDesc.横向数字验证JSON) : []) ||
                      (item.横向数字验证 && Array.isArray(item.横向数字验证) ? item.横向数字验证 : []);
    while (numberValidation.length < horizontalHeaders.length) {
      numberValidation.push(false);
    }
  } catch (e) {
    numberValidation = new Array(horizontalHeaders.length).fill(false);
  }
  
  // 根据数字验证数组转换为列类型
  const columnTypes: ColumnType[] = numberValidation.map(isNumber => isNumber ? 'number' : 'text');
  
  // 解析选项（下拉选择框和单选框）
  let options: string[][] = [];
  try {
    options = dataFormatDesc.选项 || 
             (dataFormatDesc.选项JSON ? JSON.parse(dataFormatDesc.选项JSON) : []) ||
             (item.选项 && Array.isArray(item.选项) ? item.选项 : []);
    while (options.length < horizontalHeaders.length) {
      options.push([]);
    }
  } catch (e) {
    options = new Array(horizontalHeaders.length).fill([]);
  }

  let autoFillRowCounts: number[] = [];
  try {
    autoFillRowCounts = dataFormatDesc.自动填充行数 ||
      (dataFormatDesc.自动填充行数JSON ? JSON.parse(dataFormatDesc.自动填充行数JSON) : []) ||
      [];
    while (autoFillRowCounts.length < horizontalHeaders.length) autoFillRowCounts.push(0);
  } catch (e) {
    autoFillRowCounts = new Array(horizontalHeaders.length).fill(0);
  }
  
  // 根据选项更新列类型
  options.forEach((optionList, colIndex) => {
    if (optionList && optionList.length > 0) {
      if (optionList[0] === TABLE_PRESET_VALUE_MARKER) {
        columnTypes[colIndex] = 'preset';
        options[colIndex] = optionList.slice(1);
      } else if (optionList.length === 1 && optionList[0] === '是') {
        columnTypes[colIndex] = 'radio';
      } else {
        columnTypes[colIndex] = 'select';
      }
    }
  });
  
  // 解析特殊属性集合
  let specialAttributes: SpecialAttributeItem[] = [];
  try {
    const attrList = item.检测项目特殊属性集合 || [];
    specialAttributes = attrList.map((attr: Record<string, unknown>) => {
      const title = (attr.标题 as string) || (attr.名称 as string) || '';
      // 解析选项：优先从 选项 数组获取，其次从 选项json 解析
      let options: string[] = [];
      if (Array.isArray(attr.选项) && attr.选项.length > 0) {
        options = attr.选项 as string[];
      } else if (attr.选项json && typeof attr.选项json === 'string') {
        try {
          options = JSON.parse(attr.选项json as string);
        } catch {
          options = [];
        }
      }
      // 是否属于单条数据（false/缺省 = 全局属性）
      const isSingle = attr.是否属于单条数据 === true || attr.isSingle === true;
      return { title, options, isSingle };
    }).filter((item: SpecialAttributeItem) => item.title);
  } catch (e) {
    specialAttributes = [];
  }
  
  // 初始化数据区域
  const data: string[][] = [];
  for (let i = 0; i < verticalHeaders.length; i++) {
    const row: string[] = [];
    for (let j = 0; j < horizontalHeaders.length; j++) {
      if (columnTypes[j] === 'preset') {
        row.push(String(options[j]?.[i] || ''));
      } else {
        row.push('');
      }
    }
    data.push(row);
  }
  
  // 保存原始检测数据格式描述的Id（如果存在）
  const originalDataFormatDescId = dataFormatDesc?.Id || dataFormatDesc?.id || undefined;

  return {
    name: item.名称 || item.name || '表格',
    controlNumber: item.受控编号 || '',
    numberSuffix: item.编号后缀 || '',
    implementDate: item.实施日期 || '',
    verticalHeaders,
    horizontalHeaders,
    columnTypes,
    selectOptions: options,
    autoFillRowCounts,
    columnBorders,
    sectionNames,
    data,
    specialAttributes,
    originalDataFormatDescId,
    type: dataFormatDesc['数据类型'] || dataFormatDesc.表格类型 || '表格',
    detectionObjectType: item.检测对象类型 || dataFormatDesc.检测对象类型 || '默认',
    deviceType: item.设备类型 || dataFormatDesc.设备类型 || '',
    remark: item.备注 || dataFormatDesc.备注 || '',
    imageCode: item.图片 || dataFormatDesc.图片代码 || '',
    imageName: item.图片名称 || dataFormatDesc.图片名称 || '',
    isCalculate: item.是否计算 ?? false,
    printOrientation: item.方向 ?? false
  };
}

/**
 * 格式化表格数据为提交格式
 */
export function formatTableDataForSave(tableData: TableData, itemId?: number | string, isNew = false) {
  // 收集检测项目特殊属性集合
  const specialAttributesList = tableData.specialAttributes.map(attr => ({
    标题: attr.title,
    内容: '',
    选项json: attr.options.length > 0 ? JSON.stringify(attr.options) : '',
    选项: attr.options,
    是否属于单条数据: attr.isSingle ?? false,
    检测项目ID: 0,
    ISDELETED: false,
    Id: 0
  }));

  // 收集横向数字验证（列类型为number的为true，否则为false）
  const numberValidation = tableData.columnTypes.map(type => type === 'number');
  
  // 收集选项（二维数组）
  const options: string[][] = tableData.columnTypes.map((type, colIndex) => {
    if (type === 'preset') {
      return [TABLE_PRESET_VALUE_MARKER, ...(tableData.selectOptions[colIndex] || [])];
    } else if (type === 'select') {
      // 下拉选择框：返回该列的下拉选项数组
      const selectOptions = tableData.selectOptions[colIndex] || [];
      return colIndex === 0
        ? selectOptions
        : selectOptions.map((option, index) => {
          const [text = ''] = String(option || '').split('|||');
          return text.trim() || `选项${index + 1}`;
        });
    } else if (type === 'radio') {
      // 单选框：返回["是"]
      return ['是'];
    } else {
      // 其他类型：返回空数组
      return [];
    }
  });

  const autoFillRowCounts = tableData.horizontalHeaders.map((_, colIndex) => (
    Math.max(0, Number(tableData.autoFillRowCounts?.[colIndex]) || 0)
  ));

  // 构建分区标题数组
  // 始终在数组头部插入第一分区名称（有值就插入值，没值就插入空字符串）
  // 这样数组长度始终为 横向标题数量 + 1
  const sectionNamesArray: string[] = [];
  const firstSectionName = tableData.sectionNames[-1] || '';
  sectionNamesArray.push(firstSectionName);
  for (let i = 0; i < tableData.horizontalHeaders.length; i++) {
    sectionNamesArray.push(tableData.sectionNames[i] || '');
  }

  const dataFormatDescription: Record<string, unknown> = {
    数据类型: tableData.type,
    横向标题: tableData.horizontalHeaders,
    横向标题JSON: JSON.stringify(tableData.horizontalHeaders),
    分区线: tableData.columnBorders,
    分区线JSON: JSON.stringify(tableData.columnBorders),
    分区标题: sectionNamesArray,
    分区标题JSON: JSON.stringify(sectionNamesArray),
    纵向标题: tableData.verticalHeaders,
    纵向标题JSON: JSON.stringify(tableData.verticalHeaders),
    横向数字验证: numberValidation,
    横向数字验证JSON: JSON.stringify(numberValidation),
    检测项目ID: 0,
    选项: options,
    选项JSON: JSON.stringify(options),
    自动填充行数: autoFillRowCounts,
    自动填充行数JSON: JSON.stringify(autoFillRowCounts),
    ISDELETED: false,
    // 编辑时保留原始的Id，新增时为0
    Id: !isNew && tableData.originalDataFormatDescId ? tableData.originalDataFormatDescId : 0,
    表格类型: tableData.type || '表格',
    检测对象类型: tableData.detectionObjectType || '',
    设备类型: tableData.deviceType || '',
    备注: tableData.remark || '',
    图片代码: tableData.imageCode || '',
    图片名称: tableData.imageName || ''
  };

  // 构建完整的提交数据
  const submitData: Record<string, unknown> = {
    名称: tableData.name,
    受控编号: tableData.controlNumber || '',
    编号后缀: tableData.numberSuffix || '',
    实施日期: tableData.implementDate || '',
    检测原始数据主表id: 0,
    检测项目包id: 0,
    转跳方向: true,
    是否计算: tableData.isCalculate ?? false,
    方向: tableData.printOrientation ?? false,
    数量: 0,
    验证数量: true,
    图片: tableData.imageCode || '',
    检测对象类型: tableData.detectionObjectType || '',
    设备类型: tableData.deviceType || '',
    备注: tableData.remark || '',
    检测项目特殊属性集合: specialAttributesList,
    检测数据格式描述: tableData.type === '图片' ? {'数据类型': tableData.type} : dataFormatDescription,
    ISDELETED: false,
  };

  // 如果是编辑操作，添加Id字段（需要转换为数字类型）；如果是新增，不添加Id
  if (!isNew && itemId) {
    submitData.Id = typeof itemId === 'string' ? parseInt(itemId) || itemId : itemId;
  } else if (isNew) {
    // 新增时 Id 应该为 0
    submitData.Id = 0;
  }

  return submitData;
}

/**
 * 获取今天的日期字符串（YYYY-MM-DD格式）
 */
function getTodayDateString(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
  
/**
 * 创建默认表格数据
 */
export function createDefaultTableData(name?: string): TableData {
  return {
    name: name || '表格1',
    controlNumber: '',
    numberSuffix: '',
    implementDate: '',
    verticalHeaders: ['行1'],
    horizontalHeaders: ['列1'],
    columnTypes: ['text'],
    selectOptions: [[]],
    autoFillRowCounts: [0],
    columnBorders: [false],
    sectionNames: {},
    data: [['']],
    specialAttributes: [],
    type: '表格',
    detectionObjectType: '默认',
    deviceType: '',
    remark: '',
    imageCode: '',
    imageName: '',
    isCalculate: false,
    printOrientation: false
  };
}

